// Calendar helpers shared by every derivation that speaks about days ("hoy", "ayer", "lun",
// day separators). Everything takes an explicit `timeZone` so tests are deterministic and a
// derivation never reads the machine clock behind the caller's back (the iOS `isDateInToday`
// trap recorded in MessageListDerivation.swift: the `now` parameter must be the only "now").

/** The machine's IANA zone, or undefined to let Intl decide. */
export function localTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? '';
  let f = partsCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      weekday: 'short',
    });
    partsCache.set(key, f);
  }
  return f;
}

export interface CivilTime {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number;
  minute: number;
  second: number;
  /** 0 = Sunday … 6 = Saturday */
  weekday: number;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** The wall-clock reading of `ms` in `timeZone`. */
export function civil(ms: number, timeZone?: string): CivilTime {
  const out: Record<string, string> = {};
  for (const p of partsFormatter(timeZone).formatToParts(new Date(ms))) out[p.type] = p.value;
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
    second: Number(out.second),
    weekday: WEEKDAYS[out.weekday] ?? 0,
  };
}

/** A sortable integer naming the calendar day of `ms` in `timeZone` (yyyymmdd). */
export function dayKey(ms: number, timeZone?: string): number {
  const c = civil(ms, timeZone);
  return c.year * 10000 + c.month * 100 + c.day;
}

export function isSameDay(a: number, b: number, timeZone?: string): boolean {
  return dayKey(a, timeZone) === dayKey(b, timeZone);
}

/**
 * The calendar-day key `days` days away from `ms`'s day — done on the civil date, never by adding
 * 86,400,000 (a DST change makes a day 23 or 25 hours long).
 */
export function shiftedDayKey(ms: number, days: number, timeZone?: string): number {
  const c = civil(ms, timeZone);
  const d = new Date(Date.UTC(c.year, c.month - 1, c.day + days));
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}

/** Whole calendar days from `a`'s day to `b`'s day (b later ⇒ positive). */
export function calendarDaysBetween(a: number, b: number, timeZone?: string): number {
  const ca = civil(a, timeZone);
  const cb = civil(b, timeZone);
  const ua = Date.UTC(ca.year, ca.month - 1, ca.day);
  const ub = Date.UTC(cb.year, cb.month - 1, cb.day);
  return Math.round((ub - ua) / 86_400_000);
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();

/** A cached Intl formatter. Building one costs far more than using one, and lists format a lot. */
export function dateFormatter(locale: string, options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat {
  const key = locale + '|' + (timeZone ?? '') + '|' + JSON.stringify(options);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    fmtCache.set(key, f);
  }
  return f;
}
